/**
 * Rendering executors — render_range_image.
 *
 * Captures a Base64 PNG of a live worksheet range so the model can *see* the
 * layout, fill colors, row heights, and merged cells — instead of depending on
 * the user pasting screenshots. The Base64 reaches the model as a vision block
 * via `injectExcelRangeImagesIntoProviderPrompt` (the client-side counterpart to
 * `injectPreviewImagesIntoProviderPrompt`).
 *
 * Self-syncing — manages its own `ctx.sync()`.
 *
 * Office.js reference:
 * https://learn.microsoft.com/en-us/javascript/api/excel/excel.range#excel-excel-range-getimage-member(1)
 */

/* global Excel */

import { resolveRange } from "./utils";

/**
 * Render a range to a Base64-encoded PNG.
 *
 * Expected args:
 *   range:      string
 *   sheetName?: string
 */
export async function renderRangeImageCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;

  if (!rangeAddr) {
    throw new Error(
      "render_range_image requires a `range` argument (e.g. 'A1:D20')."
    );
  }

  const { range } = resolveRange(ctx, rangeAddr, sheetName);

  // Feature-detect: getImage is ExcelApi 1.7+. On older hosts it's absent.
  if (typeof range.getImage !== "function") {
    throw new Error(
      "Image rendering is not supported on this Excel host (requires ExcelApi 1.7+)."
    );
  }

  range.load("address,rowCount,columnCount");
  const image = range.getImage();
  await ctx.sync();

  return {
    range: rangeAddr,
    address: range.address,
    rowCount: range.rowCount,
    columnCount: range.columnCount,
    base64: image.value,
    mediaType: "image/png",
  };
}
